/**
 * EmailDraftModal — P34 (Pedro 2026-10-13/14): compose a draft email from an Agreement or a
 * Person. "Prepare email" downloads an unsent email file (.eml) that opens in Outlook as a new
 * message to check and send; the app sends nothing and never touches Outlook.
 *
 *   - To: people of the record only, by the email on their record. A person without one is
 *     shown greyed with a link to their record — no typed addresses (answer 3).
 *   - Subject / text: the standard text, kept in step with the picks until the user edits it;
 *     "Standard text" puts it back.
 *   - Attachments: on an agreement the legal documents (the Docs tab's own grid, variant
 *     'email'; generated on Prepare after the usual questions) and the monthly reports;
 *     documents on file (on an agreement: also its accounts' and policies'), the Presentation
 *     Library, blank legal templates (language and format each) and other files from this
 *     computer — attached to this draft only, never stored. 20 MB in all.
 *
 * Opened ONLY from the page header (Read mode too) — Pedro 2026-10-14, option A: one way in.
 * Rendered in a portal, so the page's Read-mode fieldset does not disable it.
 */
import {
  Alert, Anchor, Badge, Button, Checkbox, Divider, FileButton, Grid, Group, Loader, Modal, MultiSelect,
  ScrollArea, SegmentedControl, Stack, Table, Text, TextInput, Textarea, Title, Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconMail, IconPaperclip, IconRotate, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { DocumentsZipRequestBody } from '../api/documentsZip';
import {
  fetchDefaultText, fetchEmailOptions, prepareEmailDraft,
  type EmailRecordType, type EmailTemplateKind, type EmailTemplatePick,
} from '../api/emailDrafts';
import { IaaExportBlockedError, downloadBlob, type IaaExportMissingRecord } from '../api/iaaExports';
import type { DocumentLanguage, UUID } from '../api/types';
import { useAgreement } from '../hooks/useAgreements';
import { AgreementDocumentsTab } from './AgreementDocumentsTab';
import { DOCS as TEMPLATE_ROWS } from './BlankLegalTemplatesCard';

/** The legal documents ticked in the dialog's grid, once their questions are answered. */
export interface GeneratedSelection {
  body: DocumentsZipRequestBody;
  labels: string[];
  /** People picked in the questions (IAA parties, whose client records): suggested for To. */
  people: string[];
}

const LANG_LABEL: Record<DocumentLanguage, string> = { PT: 'Portuguese', EN: 'English' };

function sizeLabel(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function toggled<T>(set: Set<T>, value: T, on: boolean): Set<T> {
  const next = new Set(set);
  if (on) next.add(value); else next.delete(value);
  return next;
}

export function EmailDraftModal({
  opened, onClose, recordType, recordId,
}: {
  opened: boolean;
  onClose: () => void;
  recordType: EmailRecordType;
  recordId: UUID;
}) {
  const optionsQ = useQuery({
    queryKey: ['email-draft-options', recordType, recordId],
    queryFn: () => fetchEmailOptions(recordType, recordId),
    enabled: opened,
    staleTime: 0,
  });
  const opts = optionsQ.data;
  // Agreement: the Legal documents grid needs the agreement (the Docs tab's own grid). The
  // same request as the agreement page (include_deleted) — shared cache, and never the bare
  // page address, which the browser could answer with the page's own HTML (C-367).
  const agreementQ = useAgreement(opened && recordType === 'agreement' ? recordId : undefined, true);
  // The legal documents attached from that grid, and whether ticks wait to be attached.
  const [generated, setGenerated] = useState<GeneratedSelection | null>(null);
  const [legalPending, setLegalPending] = useState(false);
  const [legalLabels, setLegalLabels] = useState<string[]>([]);
  // One step (Pedro's smoke 2026-10-14): Prepare email asks the grid to run its questions and
  // attach (attachSignal), then prepares once the attachment arrives (prepareAfterAttach).
  const [attachSignal, setAttachSignal] = useState(0);
  const [prepareAfterAttach, setPrepareAfterAttach] = useState(false);
  useEffect(() => {
    if (!opened) {
      setGenerated(null); setLegalPending(false); setLegalLabels([]);
      setAttachSignal(0); setPrepareAfterAttach(false);
    }
  }, [opened]);
  function onLegal(g: GeneratedSelection | null) {
    setGenerated(g);
    if (!g) { setPrepareAfterAttach(false); return; }
    // The people picked in the questions (IAA parties, client records) are ticked in To when
    // they have an email on file — added, never removed (smoke step 6).
    const withEmail = new Set((opts?.recipients ?? []).filter((r) => r.email).map((r) => r.person_id));
    const add = g.people.filter((p) => withEmail.has(p));
    if (add.length) setTo((prev) => new Set([...prev, ...add]));
  }

  const [language, setLanguage] = useState<DocumentLanguage>('PT');
  const [to, setTo] = useState<Set<string>>(new Set());
  // EM-CC (Pedro 2026-10-19): colleagues in copy, chosen actively — none ticked to start.
  const [cc, setCc] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [textEdited, setTextEdited] = useState(false);
  const [reports, setReports] = useState<Set<string>>(new Set());
  const [docs, setDocs] = useState<Set<string>>(new Set());
  const [library, setLibrary] = useState<Set<string>>(new Set());
  const [templates, setTemplates] = useState<Set<string>>(new Set());   // kind:lang:fmt
  const [others, setOthers] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<IaaExportMissingRecord[] | null>(null);

  // A fresh start ONCE per opening, when the options first arrive — not on every refetch
  // (the options re-fetch when the window regains focus, e.g. back from the file picker,
  // and that must not clear the user's picks).
  const initialised = useRef(false);
  useEffect(() => { if (!opened) initialised.current = false; }, [opened]);
  useEffect(() => {
    if (!opened || !opts || initialised.current) return;
    initialised.current = true;
    setLanguage(opts.language);
    // Pedro 2026-10-14 (smoke line 3): nobody ticked to start with.
    setTo(new Set());
    setCc(new Set());
    setTextEdited(false);
    setReports(new Set()); setDocs(new Set()); setLibrary(new Set()); setTemplates(new Set());
    setOthers([]); setError(null); setBlocked(null);
  }, [opened, opts, recordType]);

  // The names the standard text lists, in attachment order.
  const fileNames = useMemo(() => {
    if (!opts) return [] as string[];
    // The ticked legal documents, named as soon as they are ticked; once generated, the
    // generated names (a client record per person picked).
    const n: string[] = [...(generated?.labels ?? legalLabels)];
    (opts.monthly_reports ?? []).forEach((m) => { if (reports.has(m.period_month)) n.push(m.filename); });
    opts.documents.forEach((d) => { if (docs.has(d.id)) n.push(d.filename); });
    opts.library.forEach((l) => { if (library.has(l.id)) n.push(l.title); });
    TEMPLATE_ROWS.forEach((row) => (['PT', 'EN'] as DocumentLanguage[]).forEach((lang) =>
      (['word', 'pdf'] as const).forEach((fmt) => {
        if (templates.has(`${row.kind}:${lang}:${fmt}`)) n.push(`${row.name} (${lang}, ${fmt === 'pdf' ? 'PDF' : 'Word'})`);
      })));
    others.forEach((f) => n.push(f.name));
    return n;
  }, [opts, generated, legalLabels, reports, docs, library, templates, others]);

  const pickedPeople = useMemo(
    () => (opts?.recipients ?? []).filter((r) => to.has(r.person_id))
      .map((r) => ({ name: r.name, gender: r.gender })),
    [opts, to],
  );

  // The standard text follows the picks until the user edits subject or text.
  const [textKey] = useDebouncedValue(JSON.stringify([language, pickedPeople, fileNames]), 300);
  const defaultQ = useQuery({
    queryKey: ['email-draft-default-text', textKey],
    queryFn: () => fetchDefaultText(language, pickedPeople, fileNames),
    enabled: opened && !!opts,
  });
  useEffect(() => {
    if (!textEdited && defaultQ.data) {
      setSubject(defaultQ.data.subject);
      setBody(defaultQ.data.body);
    }
  }, [defaultQ.data, textEdited]);

  const knownBytes = useMemo(() => {
    if (!opts) return 0;
    let b = 0;
    opts.documents.forEach((d) => { if (docs.has(d.id)) b += d.size_bytes; });
    opts.library.forEach((l) => { if (library.has(l.id)) b += l.size_bytes; });
    others.forEach((f) => { b += f.size; });
    return b;
  }, [opts, docs, library, others]);
  const overLimit = !!opts && knownBytes > opts.limit_bytes;
  const anyWithEmail = (opts?.recipients ?? []).some((r) => r.email);
  // With legal documents still to generate, To may be filled by the people picked in their
  // questions, so an empty To does not block yet.
  const blockReason = to.size === 0 && !legalPending ? 'Tick at least one recipient.'
    : !subject.trim() ? 'Write a subject.'
      : overLimit ? 'The attachments pass 20 MB: untick some files.' : null;

  function onPrepareClick() {
    setError(null);
    if (legalPending) {
      // Ask the questions first; the effect below prepares when the documents are attached.
      setPrepareAfterAttach(true);
      setAttachSignal((n) => n + 1);
      return;
    }
    void prepare();
  }
  useEffect(() => {
    if (!prepareAfterAttach || !generated) return;
    setPrepareAfterAttach(false);
    if (to.size === 0) {
      setError('Tick at least one recipient: nobody picked in the questions has an email on file.');
      return;
    }
    void prepare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareAfterAttach, generated, to]);

  async function prepare() {
    if (!opts) return;
    setBusy(true); setError(null); setBlocked(null);
    const picks: EmailTemplatePick[] = [...templates].map((k) => {
      const [kind, lang, fmt] = k.split(':');
      return { kind: kind as EmailTemplateKind, language: lang as DocumentLanguage, format: fmt as 'word' | 'pdf' };
    });
    try {
      const r = await prepareEmailDraft(recordType, recordId, {
        recipients: [...to], cc_users: [...cc], subject, body,
        ...(generated ? { generated: generated.body } : {}),
        monthly_reports: [...reports], documents: [...docs], library: [...library], templates: picks,
      }, others);
      downloadBlob(r.blob, r.fileName);
      notifications.show({
        color: r.skipped ? 'orange' : 'green',
        icon: r.skipped ? <IconAlertCircle size={16} /> : <IconCheck size={16} />,
        title: 'Email draft ready',
        message: `Open ${r.fileName}: it shows a new message in Outlook to check and send.`
          + (r.skipped ? ` ${r.skipped} not attached: ${r.skippedDetail ?? ''}` : ''),
      });
      onClose();
    } catch (e) {
      if (e instanceof IaaExportBlockedError) {
        setBlocked(e.missing);
        setError(e.message);
      } else {
        setError(e instanceof Error ? e.message : 'The email draft could not be prepared.');
      }
    } finally {
      setBusy(false);
    }
  }

  const section = (title: string, children: ReactNode, hint?: string) => (
    <Stack gap={6}>
      <Group gap="xs"><Title order={6}>{title}</Title>{hint && <Text size="xs" c="dimmed">{hint}</Text>}</Group>
      {children}
    </Stack>
  );
  const none = (text: string) => <Text size="sm" c="dimmed">{text}</Text>;

  return (
    <Modal opened={opened} onClose={onClose} size="92%" yOffset="2vh"
      title={(
        <Group gap="xs">
          <IconMail size={18} />
          <Text fw={600}>Email{opts ? ` — ${opts.record.label}` : ''}</Text>
        </Group>
      )}>
      {optionsQ.isLoading || !opts ? (
        optionsQ.error
          ? <Alert color="red" icon={<IconAlertCircle size={16} />}>{(optionsQ.error as Error).message}</Alert>
          : <Group justify="center" p="lg"><Loader size="sm" /></Group>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Prepares a draft email with the files attached. It opens in Outlook as a new message:
            check it there and press Send. The app sends nothing.
          </Text>

          {/* Pedro 2026-10-14 (smoke line 3): wide and tall enough to need no scrolling —
              the message on the left, the attachments on the right. */}
          <Grid gutter="xl">
          <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="md">
          {section('To', (
            <Stack gap={4}>
              {opts.recipients.length === 0 && none(
                recordType === 'agreement' ? 'This agreement has no parties yet.' : 'No recipient.')}
              {opts.recipients.map((r) => (
                <Group key={r.person_id} gap="xs" wrap="nowrap">
                  <Checkbox size="sm" checked={to.has(r.person_id)} disabled={!r.email}
                    onChange={(e) => setTo(toggled(to, r.person_id, e.currentTarget.checked))}
                    label={(
                      <Text size="sm" c={r.email ? undefined : 'dimmed'}>
                        {r.name} <Text span size="xs" c="dimmed">· {r.roles}</Text>
                        {r.email && <Text span size="sm" c="dimmed"> — {r.email}</Text>}
                      </Text>
                    )} />
                  {!r.email && (
                    <Text size="xs" c="orange">
                      No email on file —{' '}
                      <Anchor component={Link} to={`/persons/${r.person_id}`} size="xs" onClick={onClose}>
                        add it on the person&apos;s record
                      </Anchor>
                    </Text>
                  )}
                </Group>
              ))}
              {!anyWithEmail && opts.recipients.length > 0 && (
                <Alert color="orange" icon={<IconAlertCircle size={16} />}>
                  Nobody here has an email on file. Add it on the person&apos;s record first; an
                  email can go only to an address on record.
                </Alert>
              )}
            </Stack>
          ), 'People of this record, by the email on their record')}

          {/* One line when unused (Pedro 2026-10-19): a picker whose list ticks each colleague
              chosen; the chosen ones show as chips. Empty to start. */}
          {section('Cc', (
            (opts.colleagues ?? []).length === 0 ? none('No other users.') : (
              <MultiSelect size="sm" searchable clearable checkIconPosition="left"
                placeholder={cc.size ? undefined : 'Add colleagues in copy'}
                nothingFoundMessage="No colleague by that name"
                data={(opts.colleagues ?? []).map((c) => ({ value: c.user_id, label: `${c.name} — ${c.email}` }))}
                value={[...cc]} onChange={(v) => setCc(new Set(v))} />
            )
          ), 'Colleagues in copy, chosen one by one; none to start')}

          <Group justify="space-between" align="flex-end">
            <SegmentedControl size="xs" value={language}
              onChange={(v) => setLanguage(v as DocumentLanguage)}
              data={[{ value: 'PT', label: 'Portuguese' }, { value: 'EN', label: 'English' }]} />
            <Tooltip label="Put back the standard subject and text for the current picks" withArrow>
              <Button size="xs" variant="subtle" leftSection={<IconRotate size={14} />}
                disabled={!textEdited} onClick={() => setTextEdited(false)}>
                Standard text
              </Button>
            </Tooltip>
          </Group>
          <TextInput label="Subject" value={subject} required maxLength={300}
            onChange={(e) => { setSubject(e.currentTarget.value); setTextEdited(true); }} />
          <Textarea label="Text" value={body} autosize minRows={10} maxRows={24}
            onChange={(e) => { setBody(e.currentTarget.value); setTextEdited(true); }} />
          </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 7 }}>
          <Divider mb="sm" label={<Group gap={4}><IconPaperclip size={14} /><Text size="sm">Attachments</Text></Group>}
            labelPosition="left" />

          <ScrollArea.Autosize mah="calc(100vh - 280px)" type="auto">
            <Stack gap="md" pr="sm">
              {recordType === 'agreement' && section('Legal documents', agreementQ.data ? (
                <Stack gap={6}>
                  <AgreementDocumentsTab agreement={agreementQ.data} variant="email"
                    onEmailSelection={onLegal}
                    onPendingChange={setLegalPending} onTickedLabels={setLegalLabels}
                    attachSignal={attachSignal}
                    onAttachCancelled={() => setPrepareAfterAttach(false)} />
                  {generated && (
                    <Group gap={6}>
                      {generated.labels.map((l) => <Badge key={l} variant="light" color="blue">{l}</Badge>)}
                    </Group>
                  )}
                </Stack>
              ) : <Loader size="xs" />, 'Generated when the draft is prepared, as Download does')}

              {opts.monthly_reports !== null && section('Monthly reports', opts.monthly_reports.length === 0
                ? none('No monthly report on record yet.')
                : (
                  <Group gap="md">
                    {opts.monthly_reports.map((m) => (
                      <Checkbox key={m.period_month} size="sm" label={m.period_month.slice(0, 7)}
                        checked={reports.has(m.period_month)}
                        onChange={(e) => setReports(toggled(reports, m.period_month, e.currentTarget.checked))} />
                    ))}
                  </Group>
                ))}

              {section('Documents on file', opts.documents.length === 0
                ? none(recordType === 'agreement'
                  ? 'No documents on this agreement, its accounts or its policies.'
                  : 'No documents on this person.')
                : (
                  <Stack gap={4}>
                    {opts.documents.map((d) => (
                      <Checkbox key={d.id} size="sm" checked={docs.has(d.id)}
                        onChange={(e) => setDocs(toggled(docs, d.id, e.currentTarget.checked))}
                        label={(
                          <Text size="sm">
                            {recordType === 'agreement' && <Text span size="xs" c="dimmed">{d.record_label} · </Text>}
                            {d.kind_label}{d.title ? ` — ${d.title}` : ''}{' '}
                            <Text span size="xs" c="dimmed">{d.filename} · {sizeLabel(d.size_bytes)}</Text>
                          </Text>
                        )} />
                    ))}
                  </Stack>
                ))}

              {section('Presentation Library', opts.library.length === 0
                ? none('The library is empty.')
                : (
                  <Stack gap={4}>
                    {opts.library.map((l) => (
                      <Checkbox key={l.id} size="sm" checked={library.has(l.id)}
                        onChange={(e) => setLibrary(toggled(library, l.id, e.currentTarget.checked))}
                        label={(
                          <Text size="sm">
                            {l.title} <Text span size="xs" c="dimmed">{l.filename} · {sizeLabel(l.size_bytes)}</Text>
                          </Text>
                        )} />
                    ))}
                  </Stack>
                ))}

              {section('Blank legal templates', (
                <Table verticalSpacing={4} horizontalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Template</Table.Th>
                      {(['PT', 'EN'] as DocumentLanguage[]).map((l) => <Table.Th key={l}>{LANG_LABEL[l]}</Table.Th>)}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {TEMPLATE_ROWS.map((row) => (
                      <Table.Tr key={row.kind}>
                        <Table.Td><Text size="sm">{row.name}</Text></Table.Td>
                        {(['PT', 'EN'] as DocumentLanguage[]).map((lang) => (
                          <Table.Td key={lang}>
                            <Group gap="md">
                              {(['word', 'pdf'] as const).map((fmt) => {
                                const available = fmt === 'word' ? row.word : row.pdf;
                                const key = `${row.kind}:${lang}:${fmt}`;
                                return (
                                  <Checkbox key={fmt} size="xs" disabled={!available}
                                    checked={available && templates.has(key)}
                                    onChange={(e) => setTemplates(toggled(templates, key, e.currentTarget.checked))}
                                    label={<Text size="xs" c={available ? undefined : 'dimmed'}>{fmt === 'pdf' ? 'PDF' : 'Word'}</Text>} />
                                );
                              })}
                            </Group>
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ), 'Unfilled; Annex I with the standard fee schedule')}

              {section('Other files', (
                <Stack gap={4}>
                  {others.map((f, i) => (
                    <Group key={`${f.name}-${i}`} gap="xs">
                      <Text size="sm">{f.name} <Text span size="xs" c="dimmed">· {sizeLabel(f.size)}</Text></Text>
                      <Button size="compact-xs" variant="subtle" color="gray" leftSection={<IconX size={12} />}
                        onClick={() => setOthers(others.filter((_, j) => j !== i))}>Remove</Button>
                    </Group>
                  ))}
                  <FileButton multiple onChange={(files) => setOthers([...others, ...files])}>
                    {(props) => <Button {...props} size="xs" variant="light" w="fit-content">Add files…</Button>}
                  </FileButton>
                </Stack>
              ), 'From this computer; attached to this draft only, not kept in the app')}
            </Stack>
          </ScrollArea.Autosize>

          <Text size="xs" mt="xs" c={overLimit ? 'red' : 'dimmed'}>
            {sizeLabel(knownBytes)} of the files above with a known size, of 20 MB in all
            (generated documents and templates count when the draft is prepared).
          </Text>
          </Grid.Col>
          </Grid>

          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Email draft not prepared">
              <Stack gap={4}>
                <Text size="sm">{error}</Text>
                {blocked?.map((m) => (
                  <Text key={`${m.person_id}-${m.person_name}`} size="sm">
                    {m.person_id
                      ? <Anchor component={Link} to={`/persons/${m.person_id}`} onClick={onClose}>{m.person_name}</Anchor>
                      : m.person_name}: {m.fields.join(', ')}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Tooltip label={blockReason ?? ''} disabled={!blockReason} withArrow>
              <span style={{ display: 'inline-flex' }}>
                <Button leftSection={<IconMail size={16} />} loading={busy || prepareAfterAttach}
                  onClick={onPrepareClick} disabled={!!blockReason}>
                  Prepare email
                </Button>
              </span>
            </Tooltip>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
